// client/src/coms/serveradmin/MyModeration.js
// Settings → Moderation & appeals: the member's own warnings, timeouts, kicks and bans in every server
// (including servers that banned them), with the reason they were given, and private appeals.
import React, { useState } from 'react';
import '../organisation/org.css';
import './admin.css';
import { comsApiPost } from '../comsApi';
import { ACTION_LABELS, Chip, Empty, Flash, useAction, useLoad, fmtDate, fmtAgo } from './adminKit';

function AppealThread({ id, onClose }) {
  const { data, reload } = useLoad(`/me/appeals/${id}`);
  const act = useAction();
  const [reply, setReply] = useState('');
  if (!data) return <div className="vxo-loading">Loading…</div>;
  const a = data.appeal;
  return (
    <div className="vxa-card">
      <div className="vxa-row is-spread">
        <b>Your appeal</b>
        <button type="button" className="vx-btn small" onClick={onClose}>
          Close
        </button>
      </div>
      <Flash msg={act.msg} />
      <div className="vxa-code" style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>
        {a.body}
      </div>
      {data.messages.map((m) => (
        <div key={m.id} className="vxa-small" style={{ padding: '3px 0' }}>
          <b>{m.from === 'you' ? 'You' : 'Moderators'}</b> <span className="vxa-muted">{fmtAgo(m.created_at)}</span>
          <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
        </div>
      ))}
      {a.status === 'open' ? (
        <div className="vxa-row" style={{ marginTop: 6 }}>
          <input className="vxo-input" style={{ flex: 1 }} value={reply} maxLength={2000} placeholder="Add something" onChange={(e) => setReply(e.target.value)} aria-label="Reply to moderators" />
          <button
            type="button"
            className="vx-btn small"
            disabled={!reply.trim() || act.busy}
            onClick={() =>
              act.run(() => comsApiPost(`/me/appeals/${a.id}/messages`, { body: reply }), 'Sent.').then(() => {
                setReply('');
                reload();
              })
            }
          >
            Send
          </button>
        </div>
      ) : (
        <p>
          <Chip kind={a.status === 'accepted' ? 'ok' : undefined}>{a.status === 'accepted' ? 'Accepted' : 'Not accepted'}</Chip> {a.resolution}
        </p>
      )}
    </div>
  );
}

export default function MyModeration() {
  const { data, err, reload } = useLoad('/me/moderation');
  const act = useAction();
  const [writing, setWriting] = useState(null);
  const [text, setText] = useState('');
  const [thread, setThread] = useState(null);
  if (err) return <div className="vxa-muted">{err}</div>;
  if (!data) return <div className="vxo-loading">Loading…</div>;
  return (
    <div className="vxa-root" style={{ background: 'transparent', height: 'auto' }}>
      <Flash msg={act.msg} />
      {thread ? <AppealThread id={thread} onClose={() => setThread(null)} /> : null}
      {!data.items.length ? (
        <Empty icon="🕊️" title="Nothing here">You have no warnings, timeouts, kicks or bans in any server.</Empty>
      ) : (
        data.items.map((i) => (
          <div key={i.case_id} className="vxa-card">
            <div className="vxa-row is-spread">
              <div>
                <b>{ACTION_LABELS[i.action] || i.action}</b> in <b>{i.server.name}</b>
                {i.result === 'reverted' ? <Chip kind="ok">Lifted</Chip> : null}
                <div className="vxa-small vxa-muted">
                  {fmtDate(i.created_at)}
                  {i.expires_at_ms ? ` · until ${fmtDate(i.expires_at_ms)}` : ''}
                </div>
              </div>
              {i.appeal ? (
                <button type="button" className="vx-btn small" onClick={() => setThread(i.appeal.id)}>
                  Appeal: {i.appeal.status}
                </button>
              ) : i.can_appeal ? (
                <button
                  type="button"
                  className="vx-btn small primary"
                  onClick={() => {
                    setWriting(i.case_id);
                    setText('');
                  }}
                >
                  Appeal
                </button>
              ) : null}
            </div>
            <div style={{ marginTop: 6 }}>{i.reason ? `Reason: ${i.reason}` : <span className="vxa-muted">No reason was given.</span>}</div>
            {writing === i.case_id ? (
              <div style={{ marginTop: 8 }}>
                <label className="vxo-field">
                  Why should moderators reconsider? Only the server’s moderators will read this.
                  <textarea rows={4} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
                </label>
                <div className="vxa-row">
                  <button
                    type="button"
                    className="vx-btn primary"
                    disabled={act.busy || text.trim().length < 10}
                    onClick={() =>
                      act.run(() => comsApiPost('/me/appeals', { case_id: i.case_id, body: text }), 'Appeal sent. You will see the decision here.').then((r) => {
                        if (r) {
                          setWriting(null);
                          reload();
                        }
                      })
                    }
                  >
                    Send appeal
                  </button>
                  <button type="button" className="vx-btn" onClick={() => setWriting(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}
